import { QueryClient } from '@tanstack/react-query';

import { watchPermissionChanges } from '@/lib/permission-sync';

/**
 * A HIBA HTTP-STÁTUSZA, ha kiolvasható. A ts-rest a nem-2xx választ hiba-ágon adja tovább, és az objektum
 * hordozza a `status`-t; hálózati hibánál (`TypeError: Failed to fetch`) nincs státusz → `null`.
 */
function statusOf(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null;
  const s = (error as { status?: unknown }).status;
  return typeof s === 'number' ? s : null;
}

/** A TanStack Query kliens — konzervatív default-ok egy B2B admin apphoz. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        /**
         * ÚJRAPRÓBÁLKOZÁS CSAK ATTÓL, AMI MÚLÓ LEHET.
         *
         * A 4xx a KÉRÉSRŐL mond ítéletet, nem a hálózat állapotáról: egy 403-on a jog nem jelenik meg két,
         * egy másodperccel eltolt kérés között, egy 404-re nem születik meg az erőforrás, egy 422-t nem
         * javít meg az ismétlés. Az app ezt a felületen MÁR kimondja („403 → jog-hiba, ÚJRA gomb nélkül;
         * az újrapróbálás értelmetlen" — `list-error-state.tsx`), a lekérdezés-réteg viszont ennek
         * ellentmondott: a puszta `retry: 1` MINDEN hibát ismételt.
         *
         * MÉRVE: a `/admin/apps` (ami nem-platform-adminnál SZÁNDÉKOSAN 403, és a felület helyesen kezeli)
         * lap-betöltésenként KÉT kérést indított egy helyett. Ez minden 4xx-en fennállt, az egész appban —
         * és az Odin percenkénti kérés-plafonja (600) mellett a fölösleges duplázás nem elvi kérdés: a
         * fojtás ebben a rendszerben már kétszer látszott jog-hibának.
         *
         * A 429 IS ide tartozik: fojtás alatt az azonnali ismétlés rontja a helyzetet. Ahol a visszavárás
         * indokolt, ott a SZERVER intézi (a BFF `rateLimitAware` burkolója), nem a böngésző.
         */
        retry: (failureCount, error) => {
          const status = statusOf(error);
          if (status !== null && status >= 400 && status < 500) return false;
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Az app EGYETLEN query-kliense. Azért itt (és nem a providerben) él, mert a REACT-FÁN KÍVÜL is kell:
 * a route-guard (`beforeLoad`) hookok nélkül olvassa/tölti belőle a `/session`-t, hogy a jog-ellenőrzés
 * MÁR A NAVIGÁCIÓ ELŐTT megtörténjen. A provider ugyanezt a példányt adja tovább → egy cache, nincs
 * kétszeres session-lekérés.
 */
export const queryClient = createQueryClient();

// JOG-VÁLTOZÁS → cache-eldobás. Itt kötjük be (és nem egy komponens-effektben), mert ez az EGYETLEN hely, ami
// az app teljes életciklusára garantáltan lefut, és pontosan a cache tulajdonosa. Részletek + az indoklás
// (miért a token-store a seam, miért teljes invalidálás, és miért NEM minden token-cserére):
// `lib/permission-sync.ts`.
watchPermissionChanges(queryClient);
